import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from 'axios';
import Header from '../components/Header';
import styles from './styles/SolicitacoesDistribuicao.module.css';
import Link from '../components/Link';
import PageTitle from '../components/PageTitle';

const STATUS_OPTIONS = [
    "EM ABERTO",
    "EM PRODUÇÃO",
    "CONCLUÍDO",
    "RECUSADO"
];
const FILTER_STATUS_OPTIONS = ["TODOS", ...STATUS_OPTIONS];

function formatarData(data) {
    if (!data) return "-";

    const dt = new Date(data);
    if (Number.isNaN(dt.getTime())) return data;

    return dt.toLocaleDateString("pt-BR");
}

function getStatusClass(status) {
    switch (status) {
        case "EM ABERTO":
            return styles.statusAberto;
        case "EM PRODUÇÃO":
            return styles.statusSeparando;
        case "CONCLUÍDO":
            return styles.statusConcluido;
        case "RECUSADO":
            return styles.statusRecusado;
        default:
            return styles.statusDefault;
    }
}

export default function SolicitacoesDistribuicao() {
    const permissoes = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('permissoes') || 'null');
        } catch {
            return null;
        }
    }, []);
    const podeFiltrarSolicitante = Boolean(permissoes?.solicitacoes_distribuicao?.filtro_solicitante);
    const usuarioIdLogado = localStorage.getItem("usuarioId") || "";
    const usuarioNomeLogado = localStorage.getItem("usuario") || "-";

    const [lista, setLista] = useState([]);
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);

    const [usuariosFiltro, setUsuariosFiltro] = useState([]);
    const [filtroUsuario, setFiltroUsuario] = useState(() =>
        !podeFiltrarSolicitante && usuarioIdLogado ? usuarioIdLogado : ""
    );
    const [filtroProduto, setFiltroProduto] = useState("");
    const [periodoIni, setPeriodoIni] = useState("");
    const [periodoFim, setPeriodoFim] = useState("");
    const [status, setStatus] = useState("EM ABERTO");
    const [mostrarFiltros, setMostrarFiltros] = useState(false);

    const buscarUsuariosFiltro = useCallback(async () => {
        try {
            const response = await axios.get("https://provin.ind.br/api/solicitacoes_distribuicao/filtros/usuarios");
            setUsuariosFiltro(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Erro ao buscar usuários do filtro:", error);
            setUsuariosFiltro([]);
        }
    }, []);

    useEffect(() => {
        buscarUsuariosFiltro();
    }, [buscarUsuariosFiltro]);

    const buscarSolicitacoes = useCallback(async (filtros = {}) => {
        try {
            setLoading(true);

            const params = {};

            if (filtros.userId) params.userId = filtros.userId;
            if (filtros.cod_item) params.cod_item = filtros.cod_item;
            if (filtros.periodoIni) params.periodoIni = filtros.periodoIni;
            if (filtros.periodoFim) params.periodoFim = filtros.periodoFim;
            if (filtros.status && filtros.status !== "TODOS") params.status = filtros.status;

            const response = await axios.get("https://provin.ind.br/api/solicitacoes_distribuicao/lista", {
                params
            });

            setLista(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error("Erro ao buscar solicitações:", error);
            setLista([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const aplicarFiltros = useCallback(() => {
        buscarSolicitacoes({
            userId: podeFiltrarSolicitante ? filtroUsuario : usuarioIdLogado,
            cod_item: filtroProduto.trim(),
            periodoIni,
            periodoFim,
            status
        });
        setMostrarFiltros(false);
    }, [buscarSolicitacoes, podeFiltrarSolicitante, filtroUsuario, usuarioIdLogado, filtroProduto, periodoIni, periodoFim, status]);

    useEffect(() => {
        buscarSolicitacoes({
            userId: !podeFiltrarSolicitante ? usuarioIdLogado : "",
            status: "EM ABERTO"
        });
    }, [buscarSolicitacoes, podeFiltrarSolicitante, usuarioIdLogado]);

    const alterarStatus = async (id, novoStatus) => {
        try {
            setUpdatingId(id);

            await axios.put(`https://provin.ind.br/api/solicitacoes_distribuicao/${id}/status`, {
                status: novoStatus
            });

            setLista((prev) =>
                prev.map((item) =>
                    item.id === id ? { ...item, status: novoStatus } : item
                )
            );
        } catch (error) {
            console.error("Erro ao atualizar status:", error);
        } finally {
            setUpdatingId(null);
        }
    };

    const limparFiltros = () => {
        setFiltroUsuario(!podeFiltrarSolicitante && usuarioIdLogado ? usuarioIdLogado : "");
        setFiltroProduto("");
        setPeriodoIni("");
        setPeriodoFim("");
        setStatus("EM ABERTO");
        setMostrarFiltros(false);
    };

    const resumo = useMemo(() => {
        const total = lista.length;
        const estoqueInsuficiente = lista.filter(
            (item) => Number(item.estoque || 0) < Number(item.qtd || 0)
        ).length;
        const concluidos = lista.filter((item) => item.status === "CONCLUÍDO").length;
        const separando = lista.filter((item) => item.status === "SEPARANDO").length;

        return {
            total,
            estoqueInsuficiente,
            concluidos,
            separando
        };
    }, [lista]);

    return (
        <div>
            <Header />

            <Link redirectTo="/home" />

            <div className={styles.container}>
                <PageTitle src="/images/common/solicitacoesDistribuicao.png" title="Solicitações de Distribuição" />

                <div className={styles.summaryGrid}>
                    <div className={styles.summaryCard}>
                        <span className={styles.summaryLabel}>Solicitações</span>
                        <strong className={styles.summaryValue}>{resumo.total}</strong>
                    </div>

                    <div className={styles.summaryCard}>
                        <span className={styles.summaryLabel}>Separando</span>
                        <strong className={styles.summaryValue}>{resumo.separando}</strong>
                    </div>

                    <div className={styles.summaryCard}>
                        <span className={styles.summaryLabel}>Concluídos</span>
                        <strong className={styles.summaryValue}>{resumo.concluidos}</strong>
                    </div>

                    <div className={styles.summaryCard}>
                        <span className={styles.summaryLabel}>Estoque insuficiente</span>
                        <strong className={styles.summaryValueDanger}>
                            {resumo.estoqueInsuficiente}
                        </strong>
                    </div>
                </div>

                <div className={styles.filtersCard}>
                    <div className={styles.filtersHeader}>
                        <div>
                            <h2 className={styles.filtersTitle}>Filtros</h2>
                            <p className={styles.filtersSubtitle}>
                                Refine a busca por solicitante, produto, período e andamento.
                            </p>
                        </div>

                        <div className={styles.filtersActionsTop}>
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={() => setMostrarFiltros(!mostrarFiltros)}
                            >
                                {mostrarFiltros ? "Ocultar filtros" : "Exibir filtros"}
                            </button>

                            {mostrarFiltros && (
                                <>
                                    <button
                                        type="button"
                                        className={styles.secondaryButton}
                                        onClick={limparFiltros}
                                    >
                                        Limpar filtros
                                    </button>

                                    <button
                                        type="button"
                                        className={styles.primaryButton}
                                        onClick={aplicarFiltros}
                                    >
                                        Buscar
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {mostrarFiltros && (
                        <div className={styles.filtersGrid}>
                            {podeFiltrarSolicitante && (
                                <div className={styles.fieldGroup}>
                                    <label htmlFor="filtroUsuario">Solicitante</label>
                                    <select
                                        id="filtroUsuario"
                                        value={filtroUsuario}
                                        onChange={(e) => setFiltroUsuario(e.target.value)}
                                    >
                                        <option value="">Todos os solicitantes</option>

                                        {usuariosFiltro.map((item) => (
                                            <option key={item.userid} value={item.userid}>
                                                {item.usuario}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {!podeFiltrarSolicitante && (
                                <div className={styles.fieldGroup}>
                                    <label>Solicitante</label>
                                    <div className={styles.valorFixo}>
                                        {usuarioNomeLogado}
                                    </div>
                                </div>
                            )}

                            <div className={styles.fieldGroup}>
                                <label htmlFor="filtroProduto">Código do produto</label>
                                <input
                                    id="filtroProduto"
                                    type="text"
                                    placeholder="Digite o código"
                                    value={filtroProduto}
                                    onChange={(e) => setFiltroProduto(e.target.value)}
                                />
                            </div>

                            <div className={styles.fieldGroup}>
                                <label htmlFor="periodoIni">Período inicial</label>
                                <input
                                    id="periodoIni"
                                    type="date"
                                    value={periodoIni}
                                    onChange={(e) => setPeriodoIni(e.target.value)}
                                />
                            </div>

                            <div className={styles.fieldGroup}>
                                <label htmlFor="periodoFim">Período final</label>
                                <input
                                    id="periodoFim"
                                    type="date"
                                    value={periodoFim}
                                    onChange={(e) => setPeriodoFim(e.target.value)}
                                />
                            </div>

                            <div className={styles.fieldGroup}>
                                <label htmlFor="status">Status</label>
                                <select
                                    id="status"
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                >
                                    {FILTER_STATUS_OPTIONS.map((item) => (
                                        <option key={item} value={item}>
                                            {item}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.listSection}>
                    <div className={styles.listHeader}>
                        <div>
                            <h2 className={styles.listTitle}>Lista de Solicitações</h2>
                            {/* <p className={styles.listSubtitle}>
                                Uma linha por produto solicitado.
                            </p> */}
                        </div>

                        <div className={styles.listMeta}>
                            {loading ? "Carregando..." : `${lista.length} registro(s)`}
                        </div>
                    </div>

                    {loading ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>⏳</div>
                            <h3>Buscando solicitações</h3>
                            <p>Aguarde enquanto os dados são carregados.</p>
                        </div>
                    ) : lista.length === 0 ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>📦</div>
                            <h3>Nenhuma solicitação encontrada</h3>
                            <p>Tente ajustar os filtros para localizar outras solicitações.</p>
                        </div>
                    ) : (
                        <div className={styles.cardsGrid}>
                            {lista.map((item) => {
                                const estoque = Number(item.estoque || 0);
                                const quantidade = Number(item.qtd || 0);
                                const estoqueInsuficiente = estoque < quantidade;

                                return (
                                    <div key={item.id} className={styles.requestCard}>
                                        <div className={styles.cardTop}>
                                            <div className={styles.productBlock}>
                                                <div className={styles.imageWrap}>
                                                    <img
                                                        src={`https://provin.ind.br/api/imagem_produto/${String(
                                                            item.cod_item || ""
                                                        ).replace(/\//g, "+")}`}
                                                        alt={item.descr_longa || item.cod_item}
                                                        className={styles.productImage}
                                                    />
                                                </div>

                                                <div className={styles.productInfo}>
                                                    <div className={styles.productCodeRow}>
                                                        <strong className={styles.productCode}>
                                                            {item.cod_item}
                                                        </strong>

                                                        <span
                                                            className={`${styles.statusBadge} ${getStatusClass(
                                                                item.status
                                                            )}`}
                                                        >
                                                            {item.status || "SEM STATUS"}
                                                        </span>
                                                    </div>

                                                    <p className={styles.productDescription}>
                                                        {item.descr_longa || "Descrição não encontrada"}
                                                    </p>

                                                    <div className={styles.metaRow}>
                                                        <span>
                                                            <strong>Solicitante:</strong> {item.usuario || "-"}
                                                        </span>
                                                        <span>
                                                            <strong>Data:</strong> {formatarData(item.dt_solicitacao)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={styles.detailsCompact}>
                                            <p className={styles.detailLine}>
                                                <strong>Qtd. solicitada:</strong> {quantidade}
                                            </p>
                                            <p
                                                className={`${styles.detailLine} ${estoqueInsuficiente ? styles.detailLineDanger : ""}`}
                                            >
                                                <strong>Qtd. em estoque:</strong> {estoque}
                                            </p>
                                            {/* <p className={styles.detailLine}>
                                                <strong>ID solicitação:</strong> #{item.id}
                                            </p> */}
                                            <p className={styles.detailLine}>
                                                <strong>Anotação:</strong> {item.anotacao?.trim() || "Sem anotação informada."}
                                            </p>
                                        </div>

                                        <div className={styles.cardFooter}>
                                            <div className={styles.stockAlertArea}>
                                                {estoqueInsuficiente ? (
                                                    <span className={styles.stockAlertDanger}>
                                                        Estoque insuficiente para atender esta solicitação
                                                    </span>
                                                ) : (
                                                    <span className={styles.stockAlertOk}>
                                                        Estoque disponível para atendimento
                                                    </span>
                                                )}
                                            </div>

                                            {permissoes?.solicitacoes_distribuicao?.alterar_status && (
                                                <div className={styles.statusAction}>
                                                    <label htmlFor={`status-${item.id}`}>Alterar status</label>
                                                    <select
                                                        id={`status-${item.id}`}
                                                        value={item.status || "EM ABERTO"}
                                                        onChange={(e) => alterarStatus(item.id, e.target.value)}
                                                        disabled={updatingId === item.id}
                                                        className={styles.statusSelect}
                                                    >
                                                        {STATUS_OPTIONS.map((statusItem) => (
                                                            <option key={statusItem} value={statusItem}>
                                                                {statusItem}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
